package top.wcpe.allincore.dsl.compiler.codegen.internal

import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.StmtAst
import top.wcpe.allincore.dsl.ir.codec.ByteArrayIrWriter
import top.wcpe.allincore.dsl.ir.codec.IrByteWriter

/**
 * 把表达式 / 语句 / action 体序列化为紧凑字节流。
 *
 * 格式（与 dsl-ir codec 解耦，M5 解释器反序列化消费）：
 *
 * ```
 * ExprAst:
 *   tag(u8) | payload
 *     1 StringLit  : LP-UTF8
 *     2 NumberLit  : 8B 大端 IEEE 754 double
 *     3 BoolLit    : 1B (0/1)
 *     4 StateRef   : LP-UTF8 name
 *    34 params引用 : LP-UTF8 name
 *     5 Call       : LP-UTF8 name
 *     6 Interp     : u16 partCount, { partKind(u8)[0=Text,1=Expr] + payload }*
 *     7 BinaryOp   : LP-UTF8 op + Expr(left) + Expr(right)
 *     8 MemberAccess : Expr(target) + LP-UTF8 member
 *     9 IdentRef   : LP-UTF8 name
 *    10 ThemeRef   : LP-UTF8 key
 *    11 MemberCall : Expr(target) + LP-UTF8 method + u16 argCount + { Expr(arg) }*
 *                    + u8 hasNamedArgs + (hasNamedArgs=1: u16 nn + { LP-UTF8 name + Expr(value) }*)
 *    12 NullLit    : ∅
 *    13 UnaryOp    : op(u8)[0=NEG,1=NOT] + Expr(operand)
 *    14 SafeMemberAccess : Expr(target) + LP-UTF8 member
 *    15 SafeMemberCall   : Expr(target) + LP-UTF8 method + u16 argCount + { Expr(arg) }*
 *    16 Elvis      : Expr(left) + Expr(right)
 *    17 Ternary    : Expr(cond) + Expr(then) + Expr(else)
 *    18 LocalRef   : LP-UTF8 name
 *    19 ListLiteral: u16 elemCount + { Expr(elem) }*
 *    20 Range      : Expr(start) + Expr(end) + kind(u8)[0=CLOSED,1=HALF_OPEN]
 *    21 Index      : Expr(target) + Expr(index)
 *    22 When       : WhenPayload —— Expr(subject) + u16 branchCount
 *                    + { u16 valueCount + { Expr(v) }* + StmtList(body) }*
 *                    + u8 hasElse + (StmtList(elseBody))?
 *    23 Lambda     : u16 paramCount + { LP-UTF8 name }* + StmtList(body)
 *    24 FunRef     : LP-UTF8 name + u8 hasBound (M6-e/v13)
 *                    + (hasBound=1: u16 argCount + { Expr(arg) }*
 *                                  + u16 namedCount + { LP-UTF8 name + Expr(value) }*)
 *    25 CallExpr   : Expr(callee) + u16 argCount + { Expr(arg) }*
 *    26 BuiltinCall: LP-UTF8 name + u16 argCount + { Expr(arg) }*
 *    27 IfExpr     : Expr(cond) + StmtList(then) + StmtList(else)
 *
 * StmtAst:
 *   tag(u8) | payload
 *     1 AssignStmt : lvalueKind(u8)[0=StateLValue,1=LocalLValue] + LP-UTF8 name + Expr(rhs)
 *     2 Request    : LP-UTF8 namespace + LP-UTF8 handler
 *                    + u16 payloadCount, { LP-UTF8 key + Expr value }*
 *                    + u8 callbackCount(0-4), { Callback }*
 *    14 NetRequest : Request payload shape, decoded with netMode=true and timeoutMs=null
 *    15 NetRequestTimeout : NetRequest payload shape + Expr(timeoutMs)
 *     3 ExprStmt   : Expr(call)
 *     4 VarDecl    : LP-UTF8 name + Expr(initializer)
 *     5 If         : Expr(cond) + u16 thenCount + { stmt }* + u16 elseCount + { stmt }*
 *     6 While      : Expr(cond) + u16 bodyCount + { stmt }*
 *     7 Break      : ∅
 *     8 Continue   : ∅
 *     9 Return     : hasValue(u8) + (Expr value)?
 *    10 Try        : u16 tryCount + { stmt }*
 *                    + LP-UTF8 catchName + u16 catchCount + { stmt }*
 *                    + u16 finallyCount + { stmt }*
 *    11 ForIn      : LP-UTF8 varName + Expr(iterable) + u16 bodyCount + { stmt }*
 *    12 WhenStmt   : WhenPayload（与 Expr#22 When 同结构）
 *    13 LocalFunDecl : LP-UTF8 name + u16 paramCount + { LP-UTF8 param }*
 *                    + u16 bodyCount + { stmt }*
 *
 * Callback:
 *   kindTag(u8)[0=PENDING,1=SUCCESS,2=FAIL,3=TIMEOUT]
 *   + LP-UTF8 paramName（长度 0 表示 null/无参数）
 *   + u16 stmtCount, { stmt }*
 *
 * action body:
 *   u16 stmtCount, { stmt }*
 * ```
 *
 * LP-UTF8 = 2B 无符号长度（大端）+ UTF-8 bytes。
 * 所有数值均为大端序（IrByteWriter 大端语义，与原 DataOutputStream 一致）。
 *
 * <p>实现拆分：本对象仅负责 [ExprAst] 序列化与公共 API；语句序列化委托给 [StmtSerializer]，
 * 状态机序列化委托给 [StateMachineSerializer]。</p>
 */
internal object ExprSerializer {

    // ---- Expr 标签 ----
    private const val TAG_STRING = 1
    private const val TAG_NUMBER = 2
    private const val TAG_BOOL = 3
    private const val TAG_STATE_REF = 4
    // 编号 5（旧 TAG_CALL）已退役，不复用。
    private const val TAG_INTERP = 6
    private const val TAG_BINARY = 7
    private const val TAG_MEMBER_ACCESS = 8
    internal const val TAG_IDENT_REF = 9
    private const val TAG_THEME_REF = 10
    private const val TAG_MEMBER_CALL = 11

    // C1 新增表达式 tag。
    private const val TAG_NULL_LIT = 12
    private const val TAG_UNARY = 13
    private const val TAG_SAFE_MEMBER_ACCESS = 14
    private const val TAG_SAFE_MEMBER_CALL = 15
    private const val TAG_ELVIS = 16
    private const val TAG_TERNARY = 17
    private const val TAG_LOCAL_REF = 18

    // C2 新增表达式 tag。
    private const val TAG_LIST_LITERAL = 19
    private const val TAG_RANGE = 20
    private const val TAG_INDEX = 21
    private const val TAG_WHEN = 22

    // C3 新增表达式 tag：lambda 一等公民。
    private const val TAG_LAMBDA = 23
    private const val TAG_FUN_REF = 24
    internal const val TAG_CALL_EXPR = 25

    // C6 新增表达式 tag：内建 print/log 调用。
    private const val TAG_BUILTIN_CALL = 26

    // C7 补齐表达式 tag：if 表达式。
    private const val TAG_IF_EXPR = 27

    // M5-c 新增表达式 tag：`expr is TypeName`。
    private const val TAG_IS_EXPR = 28

    // M6-a：Map 字面量 tag 29（wire 形态见 ExprBlobReader.TAG_MAP_LITERAL）。
    private const val TAG_MAP_LITERAL = 29

    /**
     * Stage B：net.subscribe("ns.topic", handler) 节点 tag 30。
     * wire 形态见 ExprBlobReader.TAG_NET_SUBSCRIBE。
     */
    private const val TAG_NET_SUBSCRIBE = 30

    /** v24：session.<name> 表达式 tag。 */
    private const val TAG_SESSION_REF = 31

    /** v24：persist.<name> 表达式 tag。 */
    private const val TAG_PERSIST_REF = 32

    /** v25：derived.<name> 派生值引用表达式 tag。 */
    private const val TAG_DERIVED_REF = 33
    private const val TAG_PARAMS_REF = 34

    // Range kind 子标签。
    private const val RANGE_CLOSED: Int = 0
    private const val RANGE_HALF_OPEN: Int = 1

    // 一元操作子标签。
    private const val UNARY_NEG: Int = 0
    private const val UNARY_NOT: Int = 1

    // ---- Interp 片段类型 ----
    private const val INTERP_TEXT = 0
    private const val INTERP_EXPR = 1

    // ---- LValue 类型 ----
    internal const val LV_STATE = 0
    internal const val LV_LOCAL = 1
    /** M6-a：索引位置左值 `target[index] = rhs`。 */
    internal const val LV_INDEX = 2

    /** 序列化单个表达式。 */
    fun serializeExpr(expr: ExprAst): ByteArray {
        val out = ByteArrayIrWriter()
        writeExpr(out, expr)
        return out.toByteArray()
    }

    /** 序列化 action 体：D2-b 起统一走 StateMachineLowerer，无 await 时退化为普通 stmt 列表。 */
    fun serializeActionBody(stmts: List<StmtAst>): ByteArray {
        val out = ByteArrayIrWriter()
        StmtSerializer.writeBodyAsStmtList(out, stmts)
        return out.toByteArray()
    }

    internal fun writeExpr(out: IrByteWriter, expr: ExprAst) {
        when (expr) {
            is ExprAst.Literal.StringLit -> {
                out.writeByte(TAG_STRING)
                writeLpString(out, expr.value)
            }
            is ExprAst.Literal.NumberLit -> {
                out.writeByte(TAG_NUMBER)
                // IR 层数值统一为 Double（M6b Wave 3 与 theme 数值表示对齐）。
                out.writeDouble(expr.value.toDouble())
            }
            is ExprAst.Literal.BoolLit -> {
                out.writeByte(TAG_BOOL)
                out.writeByte(if (expr.value) 1 else 0)
            }
            is ExprAst.StateRef -> {
                out.writeByte(TAG_STATE_REF)
                writeLpString(out, expr.name)
            }
            is ExprAst.SessionRef -> {
                out.writeByte(TAG_SESSION_REF)
                writeLpString(out, expr.name)
            }
            is ExprAst.PersistRef -> {
                out.writeByte(TAG_PERSIST_REF)
                writeLpString(out, expr.name)
            }
            is ExprAst.DerivedRef -> {
                out.writeByte(TAG_DERIVED_REF)
                writeLpString(out, expr.name)
            }
            is ExprAst.Interp -> {
                out.writeByte(TAG_INTERP)
                require(expr.parts.size <= SerializerLimits.U16_MAX) { "插值片段数超过 65535" }
                out.writeShort(expr.parts.size)
                for (part in expr.parts) {
                    when (part) {
                        is ExprAst.Interp.InterpPart.TextPart -> {
                            out.writeByte(INTERP_TEXT)
                            writeLpString(out, part.text)
                        }
                        is ExprAst.Interp.InterpPart.ExprPart -> {
                            out.writeByte(INTERP_EXPR)
                            writeExpr(out, part.expr)
                        }
                    }
                }
            }
            is ExprAst.BinaryOp -> {
                out.writeByte(TAG_BINARY)
                writeLpString(out, expr.op)
                writeExpr(out, expr.left)
                writeExpr(out, expr.right)
            }
            // request 回调体中使用的成员访问与裸标识符。
            is ExprAst.MemberAccess -> {
                val paramsTarget = expr.target as? ExprAst.IdentRef
                if (paramsTarget?.name == "params") {
                    out.writeByte(TAG_PARAMS_REF)
                    writeLpString(out, expr.member)
                } else {
                    out.writeByte(TAG_MEMBER_ACCESS)
                    writeExpr(out, expr.target)
                    writeLpString(out, expr.member)
                }
            }
            is ExprAst.IdentRef -> {
                out.writeByte(TAG_IDENT_REF)
                writeLpString(out, expr.name)
            }
            is ExprAst.ThemeRef -> {
                out.writeByte(TAG_THEME_REF)
                writeLpString(out, expr.key)
            }
            is ExprAst.MemberCall -> {
                out.writeByte(TAG_MEMBER_CALL)
                writeExpr(out, expr.target)
                writeLpString(out, expr.method)
                require(expr.args.size <= SerializerLimits.U16_MAX) { "MemberCall 参数数超过 65535" }
                out.writeShort(expr.args.size)
                for (arg in expr.args) writeExpr(out, arg)
                // Stage 4：命名实参段（向后兼容：u8 hasNamedArgs + 可选 namedArgs 段）
                val hasNamed = expr.namedArgs.isNotEmpty()
                out.writeByte(if (hasNamed) 1 else 0)
                if (hasNamed) {
                    require(expr.namedArgs.size <= SerializerLimits.U16_MAX) { "MemberCall 命名实参数超过 65535" }
                    out.writeShort(expr.namedArgs.size)
                    for (na in expr.namedArgs) {
                        writeLpString(out, na.name)
                        writeExpr(out, na.value)
                    }
                }
            }
            is ExprAst.NullLit -> {
                out.writeByte(TAG_NULL_LIT)
            }
            is ExprAst.UnaryOp -> {
                out.writeByte(TAG_UNARY)
                val sub = when (expr.op) {
                    "-" -> UNARY_NEG
                    "!" -> UNARY_NOT
                    else -> error("未知一元运算符: ${expr.op}")
                }
                out.writeByte(sub)
                writeExpr(out, expr.operand)
            }
            is ExprAst.SafeMemberAccess -> {
                out.writeByte(TAG_SAFE_MEMBER_ACCESS)
                writeExpr(out, expr.target)
                writeLpString(out, expr.member)
            }
            is ExprAst.NonNullAssert -> {
                // M10 Stage 6：`!!` 是编译期类型断言，wire 协议不感知；序列化时直接降级为 target
                writeExpr(out, expr.target)
            }
            is ExprAst.SafeMemberCall -> {
                out.writeByte(TAG_SAFE_MEMBER_CALL)
                writeExpr(out, expr.target)
                writeLpString(out, expr.method)
                require(expr.args.size <= SerializerLimits.U16_MAX) { "SafeMemberCall 参数数超过 65535" }
                out.writeShort(expr.args.size)
                for (arg in expr.args) writeExpr(out, arg)
            }
            is ExprAst.Elvis -> {
                out.writeByte(TAG_ELVIS)
                writeExpr(out, expr.left)
                writeExpr(out, expr.right)
            }
            is ExprAst.Ternary -> {
                out.writeByte(TAG_TERNARY)
                writeExpr(out, expr.cond)
                writeExpr(out, expr.thenExpr)
                writeExpr(out, expr.elseExpr)
            }
            is ExprAst.IfExpr -> {
                out.writeByte(TAG_IF_EXPR)
                writeExpr(out, expr.cond)
                StmtSerializer.writeStmtList(out, expr.thenBody)
                StmtSerializer.writeStmtList(out, expr.elseBody)
            }
            is ExprAst.LocalRef -> {
                out.writeByte(TAG_LOCAL_REF)
                writeLpString(out, expr.name)
            }
            is ExprAst.ListLiteralExpr -> {
                out.writeByte(TAG_LIST_LITERAL)
                require(expr.elements.size <= SerializerLimits.U16_MAX) { "ListLiteral 元素超过 65535" }
                out.writeShort(expr.elements.size)
                for (e in expr.elements) writeExpr(out, e)
            }
            is ExprAst.MapLiteralExpr -> {
                // M6-a：Map 字面量 wire 格式 —— u16 entryCount + { LP-UTF8 key + Expr(value) }*
                out.writeByte(TAG_MAP_LITERAL)
                require(expr.entries.size <= SerializerLimits.U16_MAX) { "MapLiteral 条目超过 65535" }
                out.writeShort(expr.entries.size)
                for (entry in expr.entries) {
                    writeLpString(out, entry.key)
                    writeExpr(out, entry.value)
                }
            }
            is ExprAst.RangeExpr -> {
                out.writeByte(TAG_RANGE)
                writeExpr(out, expr.start)
                writeExpr(out, expr.endValue)
                out.writeByte(if (expr.kind == ExprAst.RangeKind.CLOSED) RANGE_CLOSED else RANGE_HALF_OPEN)
            }
            is ExprAst.IndexExpr -> {
                out.writeByte(TAG_INDEX)
                writeExpr(out, expr.target)
                writeExpr(out, expr.index)
            }
            is ExprAst.WhenExpr -> {
                out.writeByte(TAG_WHEN)
                writeWhenPayload(out, expr)
            }
            is ExprAst.LambdaExpr -> {
                out.writeByte(TAG_LAMBDA)
                // v9：头部新增 u8 isSuspend（位于 paramCount 之前）。
                out.writeByte(if (expr.isSuspend) 1 else 0)
                require(expr.params.size <= SerializerLimits.U16_MAX) { "lambda 参数数超过 65535" }
                out.writeShort(expr.params.size)
                // M5-b：每个形参写入 LP name + u8 hasDefault + Expr?
                val lambdaDefaults = expr.defaults
                for (i in expr.params.indices) {
                    writeLpString(out, expr.params[i])
                    val def = lambdaDefaults.getOrNull(i)
                    if (def == null) {
                        out.writeByte(0)
                    } else {
                        out.writeByte(1)
                        writeExpr(out, def)
                    }
                }
                // D2-b：lambda 体独立走状态机 lowerer；含 await 时输出 1 条 StateMachine stmt
                // M4 A2 Stage 3：suspend lambda 末位 ExprStmt 转写为 var __return_value = <expr>，
                // 让 lowerer 把"末位表达式值"绑定到 record 的 __return_value 槽位，
                // runtime invokeSuspendLambda 终结后从 record 读出该槽位作为返回值。
                val body = if (expr.isSuspend) StateMachineSerializer.transformSuspendLambdaBody(expr.body) else expr.body
                StmtSerializer.writeBodyAsStmtList(out, body)
            }
            is ExprAst.FunRefExpr -> {
                out.writeByte(TAG_FUN_REF)
                writeLpString(out, expr.name)
                // M6-e/v13：尾部追加 u8 hasBound + (绑定位置/命名实参段)
                val hasBound = expr.boundArgs.isNotEmpty() || expr.boundNamedArgs.isNotEmpty()
                out.writeByte(if (hasBound) 1 else 0)
                if (hasBound) {
                    require(expr.boundArgs.size <= SerializerLimits.U16_MAX) { "FunRef boundArgs 超过 65535" }
                    out.writeShort(expr.boundArgs.size)
                    for (a in expr.boundArgs) writeExpr(out, a)
                    require(expr.boundNamedArgs.size <= SerializerLimits.U16_MAX) { "FunRef boundNamedArgs 超过 65535" }
                    out.writeShort(expr.boundNamedArgs.size)
                    for (na in expr.boundNamedArgs) {
                        writeLpString(out, na.name)
                        writeExpr(out, na.value)
                    }
                }
            }
            is ExprAst.CallExpr -> {
                out.writeByte(TAG_CALL_EXPR)
                writeExpr(out, expr.callee)
                require(expr.args.size <= SerializerLimits.U16_MAX) { "CallExpr 参数数超过 65535" }
                // v11 M5-b：头部新增 u8 hasNamedArgs；hasNamedArgs=1 时尾部追加命名实参段。
                val hasNamed = expr.namedArgs.isNotEmpty()
                out.writeByte(if (hasNamed) 1 else 0)
                out.writeShort(expr.args.size)
                for (a in expr.args) writeExpr(out, a)
                if (hasNamed) {
                    require(expr.namedArgs.size <= SerializerLimits.U16_MAX) { "CallExpr 命名实参数超过 65535" }
                    out.writeShort(expr.namedArgs.size)
                    for (na in expr.namedArgs) {
                        writeLpString(out, na.name)
                        writeExpr(out, na.value)
                    }
                }
            }
            is ExprAst.NetRequestCall -> error(
                "NetRequestCall 仅允许作为语句出现，不能作为表达式序列化（pos=${expr.position}）"
            )
            is ExprAst.AwaitExpr -> error(
                "await 表达式必须经 CPS 降级后再序列化（pos=${expr.position}）"
            )
            is ExprAst.BuiltinCallExpr -> {
                out.writeByte(TAG_BUILTIN_CALL)
                writeLpString(out, expr.name)
                require(expr.args.size <= SerializerLimits.U16_MAX) { "BuiltinCall 参数数超过 65535" }
                out.writeShort(expr.args.size)
                for (a in expr.args) writeExpr(out, a)
                // M6-h Round 2：命名实参 u16 nn + { LP-UTF8 name + Expr value }*
                require(expr.namedArgs.size <= SerializerLimits.U16_MAX) { "BuiltinCall 命名实参数超过 65535" }
                out.writeShort(expr.namedArgs.size)
                for (na in expr.namedArgs) {
                    writeLpString(out, na.name)
                    writeExpr(out, na.value)
                }
            }
            is ExprAst.IsExpr -> {
                // M5-c：`expr is TypeName` —— Expr(target) + LP-UTF8 typeName
                out.writeByte(TAG_IS_EXPR)
                writeExpr(out, expr.target)
                writeLpString(out, expr.typeName)
            }
            is ExprAst.NetSubscribeCall -> {
                // Stage B：net.subscribe("ns.topic", handler)
                // wire: TAG_NET_SUBSCRIBE + LP-UTF8 namespace + LP-UTF8 topic + Expr(handler)
                out.writeByte(TAG_NET_SUBSCRIBE)
                writeLpString(out, expr.namespace)
                writeLpString(out, expr.topic)
                writeExpr(out, expr.handler)
            }
            is ExprAst.PropsRef -> error(
                "PropsRef 应在编译期被组件展开消除，不应到达 codegen（pos=${expr.position}）"
            )
        }
    }

    /** WhenExpr / WhenStmt 共享的 payload 序列化。 */
    internal fun writeWhenPayload(out: IrByteWriter, w: ExprAst.WhenExpr) {
        writeExpr(out, w.subject)
        require(w.branches.size <= SerializerLimits.U16_MAX) { "when 分支数超过 65535" }
        out.writeShort(w.branches.size)
        for (br in w.branches) {
            require(br.values.size <= SerializerLimits.U16_MAX) { "when 分支 value 数超过 65535" }
            out.writeShort(br.values.size)
            for (v in br.values) writeExpr(out, v)
            StmtSerializer.writeStmtList(out, br.body)
        }
        if (w.elseBody == null) {
            out.writeByte(0)
        } else {
            out.writeByte(1)
            StmtSerializer.writeStmtList(out, w.elseBody)
        }
    }

    internal fun writeLpString(out: IrByteWriter, s: String) {
        // encodeToByteArray 与 toByteArray(Charsets.UTF_8) 同为 UTF-8 编码，字节一致（FR-UI-061 中立化）
        val bytes = s.encodeToByteArray()
        require(bytes.size <= SerializerLimits.U16_MAX) { "字符串长度超过 65535: ${bytes.size}" }
        out.writeShort(bytes.size)
        out.writeBytes(bytes)
    }
}
